import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { router, useFocusEffect, useGlobalSearchParams, useSegments } from 'expo-router';
import { useMemo, useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Comment } from './Comment';
import { useCreateComment } from '../../hooks/useCreateComment';
import { useRefetchComments } from '../../hooks/useRefetchComments';
import { getCommentIsAnonymous } from '../../model/authorDisplay';
import { commentsHeading, countWithReplies } from '../../model/threadCount';
import type { AnyComment } from '../../model/types';
import { CommentBox } from '../composer/CommentBox';

import { CommentsSkeleton } from '@/components/CommentsSkeleton';
import { Heading } from '@/components/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { LibraryPost } from '@/features/inquiry-library/model';
import { useToaster } from '@/hooks/useToaster';
import { trackCommentSubmitted } from '@/util/analyticsEvents';

type ActiveReply = {
  parentId: number;
  replyToId: number;
  replyToUser: AnyComment['user'];
  isTopLevelReply: boolean;
  isAnonymous: boolean;
};

export const Comments = ({
  isLoading,
  comments,
  post,
  onReply,
  onCommentPosted,
}: {
  isLoading: boolean;
  isError: boolean;
  comments?: AnyComment[];
  post: LibraryPost | undefined;
  onReply?: (comment: AnyComment) => void;
  /**
   * Called once a comment is saved, so the screen can refresh what shows the
   * comment count. Comments don't know the Inquiry Library's cache keys.
   */
  onCommentPosted?: (postId: number) => void;
}) => {
  const { commentId } = useGlobalSearchParams<{ commentId?: string }>();

  const [activeReply, setActiveReply] = useState<ActiveReply | null>(null);

  const scrollViewRef = useRef<ScrollView>(null);

  const toast = useToaster();

  const refetchComments = useRefetchComments();

  const createCommentMutation = useCreateComment({
    onPosted: (variables) => {
      onCommentPosted?.(variables.postId);

      refetchComments(variables.postId).then(() => {
        const ref = scrollViewRef.current;

        if (!ref) {
          return;
        }

        if (!isWeb && ref instanceof HTMLElement) {
          ref.scrollIntoView({ behavior: 'smooth', block: 'end' });

          return;
        }

        ref.scrollToEnd?.({ animated: true });
      });
    },
    onFailed: (error) => {
      console.error('Error creating comment:', error);
    },
  });

  const segments = useSegments();

  const onlyScrollIfPathIsSecure = segments[0] === '(secure)';

  const commentMap = useMemo(() => {
    if (!comments) {
      return new Map<number, AnyComment>();
    }

    const map = new Map<number, AnyComment>();

    comments.forEach((comment) => {
      map.set(comment.id, comment);

      if ('replies' in comment && comment.replies) {
        comment.replies.forEach((reply) => map.set(reply.id, reply));
      }
    });

    return map;
  }, [comments]);

  useFocusEffect(() => {
    if (comments?.length === 0 || !commentId || !onlyScrollIfPathIsSecure) {
      return;
    }

    const id = Number(commentId);

    if (isWeb) {
      const comment = document.getElementById(`comment-${id}`);

      if (comment) {
        router.setParams({ commentId: undefined });

        comment.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  if (isLoading) {
    return <CommentsSkeleton commentCount={post?.comments} />;
  }

  if (!post) {
    return null;
  }

  const handleReply = (comment: AnyComment) => {
    const parentId = comment.parentId !== null ? comment.parentId : comment.id;

    const isTopLevelReply = comment.parentId === null;

    if (!isWeb) {
      onReply?.(comment);

      return;
    }

    const commentIsAnonymous = getCommentIsAnonymous(comment);

    setActiveReply({
      parentId,
      replyToId: comment.id,
      replyToUser: comment.user,
      isTopLevelReply,
      isAnonymous: commentIsAnonymous,
    });
  };

  if (!comments || comments?.length === 0) {
    return (
      <VStack className="flex-1 items-center justify-center bg-background-50 px-5 py-10">
        <Heading size="h3-md" className="mb-4 font-medium text-typography-800">
          No comments yet
        </Heading>
        <Text className="text-typography-600">Start the conversation!</Text>
      </VStack>
    );
  }

  return (
    <ScrollView
      className={twJoin(
        'flex-1 pb-2 pt-6',
        // On web the thread shares the post's 768px reading column and its gutters.
        isWeb ? 'mx-auto w-full max-w-3xl px-4 md:px-6' : 'px-5'
      )}
      ref={scrollViewRef}>
      <Heading size="h2-md" className="mb-6 font-medium text-typography-900">
        {commentsHeading(countWithReplies(comments) ?? post?.comments ?? 0)}
      </Heading>
      {comments?.map((comment: AnyComment, index) => (
        <VStack key={comment.id}>
          <Comment
            postId={post.id}
            comment={comment}
            isReply={comment.parentId !== null}
            onReply={handleReply}
            commentMap={commentMap}
          />
          {activeReply?.parentId === comment.id && (
            <CommentBox
              isReply
              isTopLevelReply={activeReply.isTopLevelReply}
              data={post}
              replyToUser={
                activeReply.isAnonymous
                  ? { firstName: '', lastName: '' }
                  : {
                      firstName: activeReply.replyToUser.firstName ?? '',
                      lastName: activeReply.replyToUser.lastName ?? '',
                    }
              }
              onCancel={() => setActiveReply(null)}
              onSubmit={async ({ body, isAnonymous }: { body: string; isAnonymous: boolean }) => {
                if (!post) {
                  return false;
                }

                try {
                  trackCommentSubmitted({
                    postId: post.id,
                    isReply: !!activeReply.replyToId,
                    isAnonymous,
                  });

                  await createCommentMutation.mutateAsync({
                    body,
                    postId: post.id,
                    replyToId: activeReply.replyToId,
                    parentId: activeReply.parentId,
                    isAnonymous,
                  });

                  setActiveReply(null);

                  return true;
                } catch {
                  toast.showError({
                    title: 'Failed to Submit Comment',
                    message: "We couldn't submit your comment. Please try again.",
                  });

                  return false;
                }
              }}
            />
          )}
        </VStack>
      ))}
    </ScrollView>
  );
};
